import React, { useEffect, useState } from 'react';
import {
  Shield,
  Users,
  CheckCircle2,
  Lock,
  Edit3,
  Search,
  Plus,
  Filter,
  KeyRound,
  ShieldCheck,
  UserCheck,
  UserX,
  RefreshCw,
  Layers,
  Database,
  Code2,
  Copy,
  Check,
  AlertCircle,
  ExternalLink,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Trash2,
  Loader2
} from 'lucide-react';
import {
  UserPermissionProfile,
  GroupWithPrograms,
  SystemProgram,
  userManagementService,
  CONTROLLER_TO_PERMISSIONS
} from '../services/userManagementService';
import { EditarPermissoesModal } from '../components/EditarPermissoesModal';
import { EditarGrupoModal } from '../components/EditarGrupoModal';
import { NovoUsuarioModal } from '../components/NovoUsuarioModal';
import { EditarUsuarioModal } from '../components/EditarUsuarioModal';
import { usePermissionsContext } from '../contexts/PermissionsContext';

export const ModuloPermissoes: React.FC = () => {
  const { reloadPermissions } = usePermissionsContext();

  const [activeTab, setActiveTab] = useState<'usuarios' | 'grupos' | 'programas' | 'migracao'>('usuarios');
  const [usuarios, setUsuarios] = useState<UserPermissionProfile[]>([]);
  const [grupos, setGrupos] = useState<GroupWithPrograms[]>([]);
  const [programas, setProgramas] = useState<SystemProgram[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros e Busca de Usuários
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroRole, setFiltroRole] = useState<string>('todos');
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');

  // Paginação de Usuários
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filtros e Paginação de Programas/Módulos
  const [programSearchTerm, setProgramSearchTerm] = useState('');
  const [programCurrentPage, setProgramCurrentPage] = useState(1);
  const [programItemsPerPage, setProgramItemsPerPage] = useState(15);

  // Filtros de Grupos
  const [groupSearchTerm, setGroupSearchTerm] = useState('');

  // Modais
  const [selectedUser, setSelectedUser] = useState<UserPermissionProfile | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupWithPrograms | null>(null);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isNewUserModalOpen, setIsNewUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<UserPermissionProfile | null>(null);
  const [userToDelete, setUserToDelete] = useState<UserPermissionProfile | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sincronizandoFirebase, setSincronizandoFirebase] = useState(false);

  // Estado de cópia do SQL
  const [copiado, setCopiado] = useState(false);
  const [syncStatus, setSyncStatus] = useState<string | null>(null);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [usersData, groupsData, programsData] = await Promise.all([
        userManagementService.getUsuariosComPermissoes(),
        userManagementService.getSystemGroups(),
        userManagementService.getSystemPrograms()
      ]);
      setUsuarios(usersData);
      setGrupos(groupsData);
      setProgramas(programsData);
    } catch (err) {
      console.error('Erro ao carregar permissões do Supabase:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const handleOpenDeleteUser = (user: UserPermissionProfile) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      // Exclui no Firestore, Supabase e backend
      await userManagementService.excluirUsuario(
        userToDelete.id,
        userToDelete.email,
        userToDelete.auth_user_id || undefined
      );

      // Remove imediatamente da lista em memória para feedback instantâneo
      setUsuarios((prev) => prev.filter((u) => u.id !== userToDelete.id && u.email?.toLowerCase() !== userToDelete.email?.toLowerCase()));
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      await carregarDados();
      reloadPermissions();
    } catch (err) {
      console.error('Erro ao excluir usuário:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  
  const handleOpenEditUser = (user: UserPermissionProfile) => {
    setUserToEdit(user);
    setIsEditUserModalOpen(true);
  };

  const handleOpenEdit = (user: UserPermissionProfile) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleOpenEditGroup = (group: GroupWithPrograms) => {
    setSelectedGroup(group);
    setIsGroupModalOpen(true);
  };

  const handleCopiarSQL = () => {
    const sql = userManagementService.getMigrationSQL();
    navigator.clipboard.writeText(sql);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const handleSincronizarFirebase = async () => {
    setSincronizandoFirebase(true);
    setSyncStatus('Sincronizando e restaurando usuários do Firebase...');
    try {
      const res = await userManagementService.sincronizarUsuariosFirebase();
      if (res.success) {
        setSyncStatus(`Sucesso: ${res.message || 'Usuários sincronizados e restaurados do Firebase!'}`);
        await carregarDados();
        await reloadPermissions();
      } else {
        setSyncStatus(`Aviso: ${res.message || 'Não foi possível sincronizar todos os registros.'}`);
      }
    } catch (e: any) {
      setSyncStatus(`Erro: ${e?.message || 'Falha ao sincronizar com Firebase'}`);
    } finally {
      setSincronizandoFirebase(false);
      setTimeout(() => setSyncStatus(null), 5000);
    }
  };

  const handleSincronizarUsuario = async () => {
    setSyncStatus('Sincronizando...');
    try {
      const user = await userManagementService.sincronizarUsuarioSupabaseAuth();
      if (user) {
        setSyncStatus(`Usuário sincronizado com sucesso: #${user.id} (${user.email || user.login})`);
        await carregarDados();
        await reloadPermissions();
      } else {
        setSyncStatus('Usuário já está sincronizado ou não há sessão ativa.');
      }
    } catch (e) {
      setSyncStatus('Erro ao sincronizar usuário.');
    }
    setTimeout(() => setSyncStatus(null), 4000);
  };

  // Efeito para resetar página de usuários quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filtroRole, filtroStatus, itemsPerPage]);

  // Efeito para resetar página de programas quando filtros mudam
  useEffect(() => {
    setProgramCurrentPage(1);
  }, [programSearchTerm, programItemsPerPage]);

  // Filtragem de Usuários
  const usuariosFiltrados = usuarios.filter((u) => {
    const matchesSearch =
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.login.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.email && u.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesRole = filtroRole === 'todos' || u.role === filtroRole;
    const isUserActive = u.active === 'Y' || u.active === 'S';
    const matchesStatus =
      filtroStatus === 'todos' ||
      (filtroStatus === 'ativo' && isUserActive) ||
      (filtroStatus === 'inativo' && !isUserActive);

    return matchesSearch && matchesRole && matchesStatus;
  });

  // Paginação de Usuários
  const totalUsers = usuariosFiltrados.length;
  const totalUserPages = Math.ceil(totalUsers / itemsPerPage) || 1;
  const safeUserPage = Math.min(Math.max(currentPage, 1), totalUserPages);
  const userStartIndex = (safeUserPage - 1) * itemsPerPage;
  const userEndIndex = userStartIndex + itemsPerPage;
  const usuariosPaginados = usuariosFiltrados.slice(userStartIndex, userEndIndex);

  // Filtragem de Programas
  const programasFiltrados = programas.filter((p) => {
    const term = programSearchTerm.toLowerCase();
    return (
      !term ||
      p.name.toLowerCase().includes(term) ||
      p.controller.toLowerCase().includes(term) ||
      String(p.id).includes(term)
    );
  });

  // Paginação de Programas
  const totalPrograms = programasFiltrados.length;
  const totalProgramPages = Math.ceil(totalPrograms / programItemsPerPage) || 1;
  const safeProgramPage = Math.min(Math.max(programCurrentPage, 1), totalProgramPages);
  const programStartIndex = (safeProgramPage - 1) * programItemsPerPage;
  const programEndIndex = programStartIndex + programItemsPerPage;
  const programasPaginados = programasFiltrados.slice(programStartIndex, programEndIndex);

  // Filtragem de Grupos
  const gruposFiltrados = grupos.filter((g) => {
    const term = groupSearchTerm.toLowerCase();
    return !term || g.name.toLowerCase().includes(term) || String(g.id).includes(term);
  });

  // Renderizador de Páginas para Usuários
  const renderPaginationPages = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalUserPages <= maxVisiblePages) {
      for (let i = 1; i <= totalUserPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, safeUserPage - 1);
      let end = Math.min(totalUserPages - 1, safeUserPage + 1);

      if (safeUserPage <= 3) {
        end = 4;
      } else if (safeUserPage >= totalUserPages - 2) {
        start = totalUserPages - 3;
      }

      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalUserPages - 1) pages.push('...');
      pages.push(totalUserPages);
    }

    return pages.map((page, idx) => {
      if (typeof page === 'string') {
        return (
          <span key={`ellipsis-${idx}`} className="px-2.5 py-1 text-slate-600 text-xs font-bold">
            ...
          </span>
        );
      }

      const isCurrent = page === safeUserPage;
      return (
        <button
          key={`page-btn-${page}`}
          onClick={() => setCurrentPage(page)}
          className={`min-w-[32px] h-8 px-2.5 rounded-lg text-xs font-bold transition flex items-center justify-center ${
            isCurrent
              ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20 font-black'
              : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
          }`}
        >
          {page}
        </button>
      );
    });
  };

  // Renderizador de Páginas para Programas
  const renderProgramPaginationPages = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalProgramPages <= maxVisiblePages) {
      for (let i = 1; i <= totalProgramPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, safeProgramPage - 1);
      let end = Math.min(totalProgramPages - 1, safeProgramPage + 1);

      if (safeProgramPage <= 3) {
        end = 4;
      } else if (safeProgramPage >= totalProgramPages - 2) {
        start = totalProgramPages - 3;
      }

      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalProgramPages - 1) pages.push('...');
      pages.push(totalProgramPages);
    }

    return pages.map((page, idx) => {
      if (typeof page === 'string') {
        return (
          <span key={`p-ellipsis-${idx}`} className="px-2.5 py-1 text-slate-600 text-xs font-bold">
            ...
          </span>
        );
      }

      const isCurrent = page === safeProgramPage;
      return (
        <button
          key={`p-page-btn-${page}`}
          onClick={() => setProgramCurrentPage(page)}
          className={`min-w-[32px] h-8 px-2.5 rounded-lg text-xs font-bold transition flex items-center justify-center ${
            isCurrent
              ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20 font-black'
              : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
          }`}
        >
          {page}
        </button>
      );
    });
  };

  return (
    <div className="space-y-6 text-left">
      {/* Header com Ações & Métricas */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-5 sm:p-6 rounded-3xl shadow-xl">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-indigo-600/20 text-indigo-400 flex items-center justify-center border border-indigo-500/30 shrink-0">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-black text-white">Controle de Permissões & Migração Supabase</h2>
              <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-indigo-950 text-indigo-300 border border-indigo-800">
                Arquitetura RBAC Relacional
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">
              Tabelas integradas: `system_users`, `system_group`, `system_program`, `system_group_program` e `auth.users`.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSincronizarFirebase}
            disabled={sincronizandoFirebase || loading}
            className="px-3.5 py-2.5 rounded-xl bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/40 text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-sm disabled:opacity-50"
            title="Restaura usuários e sincroniza perfis diretamente do Firebase"
          >
            <RefreshCw className={`w-4 h-4 text-indigo-400 ${sincronizandoFirebase ? 'animate-spin' : ''}`} />
            <span>Restaurar / Sincronizar Firebase</span>
          </button>

          <button
            onClick={carregarDados}
            disabled={loading}
            className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition cursor-pointer"
            title="Atualizar dados"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <button
            onClick={handleSincronizarUsuario}
            className="px-3.5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-indigo-300 border border-slate-700 text-xs font-bold transition flex items-center gap-1.5"
            title="Garante que seu usuário Supabase Auth está gravado em system_users"
          >
            <Sparkles className="w-4 h-4 text-indigo-400" />
            <span>Sincronizar Meu Usuário</span>
          </button>

          <button
            onClick={() => setIsNewUserModalOpen(true)}
            className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-lg shadow-red-600/20"
          >
            <Plus className="w-4 h-4" />
            <span>Novo Usuário</span>
          </button>
        </div>
      </div>

      {syncStatus && (
        <div className="p-3 rounded-2xl bg-indigo-950/80 border border-indigo-800 text-indigo-200 text-xs font-bold flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-indigo-400 shrink-0" />
          <span>{syncStatus}</span>
        </div>
      )}

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Usuários do Sistema</div>
            <div className="text-2xl font-black text-white mt-1">{usuarios.length}</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-slate-800 text-slate-300 flex items-center justify-center">
            <Users className="w-5 h-5" />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Grupos (Perfis)</div>
            <div className="text-2xl font-black text-indigo-400 mt-1">{grupos.length}</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-indigo-950/60 text-indigo-400 border border-indigo-800/40 flex items-center justify-center">
            <Layers className="w-5 h-5" />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Programas / Rotas</div>
            <div className="text-2xl font-black text-emerald-400 mt-1">{programas.length}</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-emerald-950/60 text-emerald-400 border border-emerald-800/40 flex items-center justify-center">
            <Database className="w-5 h-5" />
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Supabase Auth</div>
            <div className="text-2xl font-black text-amber-400 mt-1">Conectado</div>
          </div>
          <div className="w-10 h-10 rounded-xl bg-amber-950/60 text-amber-400 border border-amber-800/40 flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Sub-Abas do Módulo */}
      <div className="border-b border-slate-800 flex gap-4 overflow-x-auto">
        <button
          onClick={() => setActiveTab('usuarios')}
          className={`pb-3 px-2 text-xs font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${
            activeTab === 'usuarios'
              ? 'border-indigo-500 text-indigo-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          Usuários & Permissões ({usuarios.length})
        </button>

        <button
          onClick={() => setActiveTab('grupos')}
          className={`pb-3 px-2 text-xs font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${
            activeTab === 'grupos'
              ? 'border-indigo-500 text-indigo-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Layers className="w-4 h-4" />
          Grupos de Acesso `system_group` ({grupos.length})
        </button>

        <button
          onClick={() => setActiveTab('programas')}
          className={`pb-3 px-2 text-xs font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${
            activeTab === 'programas'
              ? 'border-indigo-500 text-indigo-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Database className="w-4 h-4" />
          Módulos `system_program` ({programas.length})
        </button>

        <button
          onClick={() => setActiveTab('migracao')}
          className={`pb-3 px-2 text-xs font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap ${
            activeTab === 'migracao'
              ? 'border-indigo-500 text-indigo-400'
              : 'border-transparent text-slate-400 hover:text-white'
          }`}
        >
          <Code2 className="w-4 h-4" />
          Script SQL de Migração Supabase
        </button>
      </div>

      {/* ABA 1: USUÁRIOS */}
      {activeTab === 'usuarios' && (
        <div className="space-y-4">
          {/* Filtros e Busca */}
          <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="relative w-full md:max-w-md">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome, login ou e-mail..."
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:border-indigo-500 outline-none"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
              <select
                value={filtroRole}
                onChange={(e) => setFiltroRole(e.target.value)}
                className="px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300 outline-none cursor-pointer"
              >
                <option value="todos">Todos os Níveis (Roles)</option>
                <option value="admin">Administrador</option>
                <option value="manager">Gerente / Operador</option>
                <option value="user">Usuário Padrão</option>
              </select>

              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300 outline-none cursor-pointer"
              >
                <option value="todos">Todos os Status</option>
                <option value="ativo">Ativos</option>
                <option value="inativo">Inativos</option>
              </select>

              <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium ml-1">
                <span>Exibir:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="px-2 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300 font-bold outline-none cursor-pointer"
                >
                  <option value={10}>10 por pág.</option>
                  <option value={25}>25 por pág.</option>
                  <option value={50}>50 por pág.</option>
                  <option value={100}>100 por pág.</option>
                </select>
              </div>
            </div>
          </div>

          {/* Tabela de Usuários */}
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950/80 text-slate-400 font-bold uppercase tracking-wider border-b border-slate-800">
                  <tr>
                    <th className="px-5 py-3.5">Usuário & ID</th>
                    <th className="px-4 py-3.5">Login / E-mail</th>
                    <th className="px-4 py-3.5">Vínculo Supabase Auth</th>
                    <th className="px-4 py-3.5">Grupos (`system_user_group`)</th>
                    <th className="px-4 py-3.5">Programas Efetivos</th>
                    <th className="px-4 py-3.5">Status</th>
                    <th className="px-5 py-3.5 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                        Carregando usuários do Supabase...
                      </td>
                    </tr>
                  ) : usuariosPaginados.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                        Nenhum usuário encontrado com os filtros selecionados.
                      </td>
                    </tr>
                  ) : (
                    usuariosPaginados.map((u) => {
                      const isActive = u.active === 'Y' || u.active === 'S';
                      const progCount = u.effective_programs.length;

                      return (
                        <tr key={u.id} className="hover:bg-slate-800/40 transition">
                          {/* Nome e ID */}
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-xl bg-slate-800 border border-slate-700 text-slate-300 flex items-center justify-center font-bold text-xs shrink-0">
                                {u.name.substring(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-bold text-white text-sm leading-tight">{u.name}</div>
                                <div className="text-[10px] text-slate-400 mt-0.5">ID: #{u.id}</div>
                              </div>
                            </div>
                          </td>

                          {/* Login e Email */}
                          <td className="px-4 py-4">
                            <div className="font-mono text-slate-200 font-bold">{u.login}</div>
                            <div className="text-[11px] text-slate-400 truncate max-w-[180px]">
                              {u.email || <span className="italic text-slate-600">sem e-mail</span>}
                            </div>
                          </td>

                          {/* Auth Link */}
                          <td className="px-4 py-4">
                            {u.auth_user_id ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-950/60 text-emerald-400 border border-emerald-800 text-[10px] font-mono font-bold">
                                <CheckCircle2 className="w-3 h-3" /> UUID Link
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-950 text-slate-500 border border-slate-800 text-[10px] italic">
                                Email Sync
                              </span>
                            )}
                          </td>

                          {/* Grupos */}
                          <td className="px-4 py-4">
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {u.groups.length === 0 ? (
                                <span className="text-[10px] text-slate-500 italic">Sem grupo</span>
                              ) : (
                                u.groups.map((g) => (
                                  <span
                                    key={g.id}
                                    className="px-2 py-0.5 rounded-md bg-slate-950 text-slate-300 border border-slate-800 text-[10px] font-medium"
                                  >
                                    {g.name}
                                  </span>
                                ))
                              )}
                            </div>
                          </td>

                          {/* Programas */}
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-indigo-300">{progCount} módulos</span>
                              {u.direct_programs.length > 0 && (
                                <span className="text-[10px] text-amber-400 font-bold">
                                  (+{u.direct_programs.length} diretos)
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-4 py-4">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                                isActive
                                  ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800/80'
                                  : 'bg-rose-950/80 text-rose-400 border border-rose-800/80'
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                              {isActive ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>

                          {/* Ações */}
                          <td className="px-5 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleOpenEditUser(u)}
                                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs flex items-center gap-1.5 transition shadow-sm border border-slate-700 cursor-pointer"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                                <span>Editar</span>
                              </button>
                              <button
                                onClick={() => handleOpenEdit(u)}
                                className="px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center gap-1.5 transition shadow-sm cursor-pointer"
                              >
                                <Shield className="w-3.5 h-3.5" />
                                <span>Permissões</span>
                              </button>
                              <button
                                onClick={() => handleOpenDeleteUser(u)}
                                className="p-1.5 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 text-rose-400 hover:text-rose-200 border border-rose-900/50 transition cursor-pointer"
                                title="Excluir usuário"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Rodapé de Paginação */}
            {totalUsers > 0 && (
              <div className="p-4 bg-slate-950/40 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400 font-medium">
                <div>
                  Exibindo <span className="font-bold text-white">{userStartIndex + 1}</span> a{' '}
                  <span className="font-bold text-white">{Math.min(userEndIndex, totalUsers)}</span> de{' '}
                  <span className="font-bold text-white">{totalUsers}</span> usuários
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={safeUserPage === 1}
                    className="p-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition"
                    title="Primeira página"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safeUserPage === 1}
                    className="p-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition"
                    title="Página anterior"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1 px-1">
                    {renderPaginationPages()}
                  </div>

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalUserPages, p + 1))}
                    disabled={safeUserPage === totalUserPages}
                    className="p-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition"
                    title="Próxima página"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => setCurrentPage(totalUserPages)}
                    disabled={safeUserPage === totalUserPages}
                    className="p-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition"
                    title="Última página"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ABA 2: GRUPOS */}
      {activeTab === 'grupos' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-900 border border-slate-800 p-4 rounded-2xl">
            <div className="text-xs text-slate-400">
              Grupos de segurança (`system_group`) e a relação de programas permitidos (`system_group_program`).
            </div>

            <div className="relative w-full sm:max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={groupSearchTerm}
                onChange={(e) => setGroupSearchTerm(e.target.value)}
                placeholder="Buscar por grupo..."
                className="w-full pl-9 pr-4 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>

          {gruposFiltrados.length === 0 ? (
            <div className="p-8 text-center rounded-3xl bg-slate-900 border border-slate-800 text-slate-500 text-xs">
              Nenhum grupo encontrado com o termo "{groupSearchTerm}".
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {gruposFiltrados.map((grp) => (
                <div
                  key={grp.id}
                  className="p-5 rounded-3xl bg-slate-900 border border-slate-800 flex flex-col justify-between space-y-4 shadow-lg"
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-indigo-950 text-indigo-300 border border-indigo-800">
                        Grupo #{grp.id}
                      </span>
                      <span className="text-xs font-bold text-slate-400">
                        {grp.program_ids.length} programas liberados
                      </span>
                    </div>
                    <h3 className="text-base font-black text-white mt-2">{grp.name}</h3>
                  </div>

                  <div className="p-3 rounded-2xl bg-slate-950/70 border border-slate-800/80 space-y-1.5 max-h-36 overflow-y-auto">
                    {grp.program_ids.length === 0 ? (
                      <div className="text-[11px] text-slate-500 italic">Nenhum programa vinculado ainda.</div>
                    ) : (
                      grp.program_ids.map((pid) => {
                        const prog = programas.find(p => p.id === pid);
                        return (
                          <div key={pid} className="text-[11px] text-slate-300 flex items-center gap-1.5">
                            <Check className="w-3 h-3 text-emerald-400 shrink-0" />
                            <span className="truncate">{prog ? prog.name : `Programa #${pid}`}</span>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <button
                    onClick={() => handleOpenEditGroup(grp)}
                    className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition border border-slate-700"
                  >
                    <Edit3 className="w-3.5 h-3.5 text-indigo-400" />
                    <span>Editar Programas do Grupo</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ABA 3: PROGRAMAS */}
      {activeTab === 'programas' && (
        <div className="space-y-4">
          {/* Filtros e Busca de Programas */}
          <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="relative w-full md:max-w-md">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={programSearchTerm}
                onChange={(e) => setProgramSearchTerm(e.target.value)}
                placeholder="Buscar programa por nome, ID ou controller..."
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:border-indigo-500 outline-none"
              />
            </div>

            <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium">
              <span>Exibir:</span>
              <select
                value={programItemsPerPage}
                onChange={(e) => setProgramItemsPerPage(Number(e.target.value))}
                className="px-2 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300 font-bold outline-none cursor-pointer"
              >
                <option value={15}>15 por pág.</option>
                <option value={30}>30 por pág.</option>
                <option value={50}>50 por pág.</option>
                <option value={100}>100 por pág.</option>
              </select>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-950/80 text-slate-400 font-bold uppercase tracking-wider border-b border-slate-800">
                  <tr>
                    <th className="px-5 py-3.5">ID</th>
                    <th className="px-4 py-3.5">Nome do Módulo / Programa</th>
                    <th className="px-4 py-3.5">Controller Associada</th>
                    <th className="px-4 py-3.5">Permissões React Vinculadas</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {programasPaginados.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center text-slate-500">
                        Nenhum programa encontrado com o termo selecionado.
                      </td>
                    </tr>
                  ) : (
                    programasPaginados.map((prog) => {
                      const mappedPerms = CONTROLLER_TO_PERMISSIONS[prog.controller] || [];
                      return (
                        <tr key={prog.id} className="hover:bg-slate-800/40 transition">
                          <td className="px-5 py-3.5 font-mono text-slate-400 font-bold">#{prog.id}</td>
                          <td className="px-4 py-3.5 font-bold text-white text-sm">{prog.name}</td>
                          <td className="px-4 py-3.5 font-mono text-indigo-300">{prog.controller}</td>
                          <td className="px-4 py-3.5">
                            <div className="flex flex-wrap gap-1">
                              {mappedPerms.map((p) => (
                                <span
                                  key={p}
                                  className="px-2 py-0.5 rounded bg-slate-950 border border-slate-800 text-[10px] text-slate-300 font-mono font-medium"
                                >
                                  {p}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Rodapé de Paginação de Programas */}
            {totalPrograms > 0 && (
              <div className="p-4 bg-slate-950/40 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400 font-medium">
                <div>
                  Exibindo <span className="font-bold text-white">{programStartIndex + 1}</span> a{' '}
                  <span className="font-bold text-white">{Math.min(programEndIndex, totalPrograms)}</span> de{' '}
                  <span className="font-bold text-white">{totalPrograms}</span> programas
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setProgramCurrentPage(1)}
                    disabled={safeProgramPage === 1}
                    className="p-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition"
                    title="Primeira página"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => setProgramCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safeProgramPage === 1}
                    className="p-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition"
                    title="Página anterior"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1 px-1">
                    {renderProgramPaginationPages()}
                  </div>

                  <button
                    onClick={() => setProgramCurrentPage((p) => Math.min(totalProgramPages, p + 1))}
                    disabled={safeProgramPage === totalProgramPages}
                    className="p-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition"
                    title="Próxima página"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => setProgramCurrentPage(totalProgramPages)}
                    disabled={safeProgramPage === totalProgramPages}
                    className="p-1.5 rounded-lg border border-slate-800 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-300 transition"
                    title="Última página"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ABA 4: SCRIPT DE MIGRAÇÃO SQL */}
      {activeTab === 'migracao' && (
        <div className="space-y-4">
          <div className="p-5 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-base font-black text-white flex items-center gap-2">
                  <Database className="w-5 h-5 text-indigo-400" />
                  Script de Migração & Triggers do Supabase
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Execute este script SQL diretamente no <strong>Supabase SQL Editor</strong> para criar as tabelas relacionais, vincular com `auth.users` e ativar a sincronização automática.
                </p>
              </div>

              <button
                onClick={handleCopiarSQL}
                className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold flex items-center gap-2 transition shrink-0 shadow-lg shadow-indigo-600/20"
              >
                {copiado ? <Check className="w-4 h-4 text-emerald-300" /> : <Copy className="w-4 h-4" />}
                <span>{copiado ? 'SQL Copiado!' : 'Copiar Script SQL'}</span>
              </button>
            </div>

            <div className="relative">
              <pre className="p-4 rounded-2xl bg-slate-950 border border-slate-800 text-xs font-mono text-slate-300 overflow-x-auto max-h-[500px]">
                {userManagementService.getMigrationSQL()}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Modais */}
      <EditarPermissoesModal
        user={selectedUser}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
        }}
        availableGroups={grupos}
        allPrograms={programas}
        onSaved={carregarDados}
      />

      <EditarGrupoModal
        group={selectedGroup}
        isOpen={isGroupModalOpen}
        onClose={() => {
          setIsGroupModalOpen(false);
          setSelectedGroup(null);
        }}
        allPrograms={programas}
        onSaved={carregarDados}
      />

      <EditarUsuarioModal
        isOpen={isEditUserModalOpen}
        onClose={() => {
          setIsEditUserModalOpen(false);
          setUserToEdit(null);
        }}
        user={userToEdit}
        onUserUpdated={() => {
          carregarDados();
          reloadPermissions();
        }}
      />

      <NovoUsuarioModal
        isOpen={isNewUserModalOpen}
        onClose={() => setIsNewUserModalOpen(false)}
        availableGroups={grupos}
        onUserCreated={carregarDados}
      />

      {/* Modal de Confirmação de Exclusão de Usuário */}
      {isDeleteModalOpen && userToDelete && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-md p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="w-12 h-12 rounded-2xl bg-rose-600/20 text-rose-400 flex items-center justify-center border border-rose-500/30">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="space-y-1">
              <h3 className="text-base font-bold text-white">Excluir Usuário</h3>
              <p className="text-xs text-slate-400">
                Tem certeza que deseja excluir o usuário <strong className="text-white">{userToDelete.name || userToDelete.email}</strong>?
              </p>
            </div>

            <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-800/60 text-xs text-rose-300">
              ⚠️ O usuário será removido da base, seu login no Firebase Auth e Firestore será revogado e ele perderá todo o acesso imediatamente.
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setUserToDelete(null);
                }}
                disabled={isDeleting}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteUser}
                disabled={isDeleting}
                className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold shadow-lg shadow-rose-600/30 flex items-center gap-2 transition disabled:opacity-50 cursor-pointer"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Excluindo...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    <span>Confirmar Exclusão</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
